<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// ---- validation (same style as your Tegund step) ----
const rules = {
  umsokn: {
    launaName: { required },
    launaSSN: { required },
    launaAddress: { required },
    launaPostalCode: { required },
    launaPhone: { required }
  }
}
const v$ = useVuelidate(rules, { umsokn })

// Called by ApplicationBase
async function validate(): Promise<boolean> {
  await v$.value.$validate()
  return !v$.value.$error
}
defineExpose({ validate })

// ---- helpers ----
function digitsOnly(v: string) {
  return (v || '').replace(/\D/g, '')
}
function formatKennitala(v: string) {
  const d = digitsOnly(v).slice(0, 10) // max 10 digits
  if (d.length <= 6) return d
  return `${d.slice(0, 6)}-${d.slice(6)}`
}
</script>

<template>
  <div>
    <v-row>
      <v-col>
        <h2 class="forms__header">
          {{ t('umsokn_nyr_launagreidandi_step2_header') }}
        </h2>
      </v-col>
    </v-row>

    <v-row>
      <!-- sendContract -->
      <v-col cols="12">
        <v-checkbox
          v-model="umsokn.sendContract"
          :label="t('umsokn_nyr_launagreidandi_step2_send_contract')"
          :readonly="props.disable"
          hide-details
        />
      </v-col>

      <!-- launaName -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.launaName"
          :label="t('umsokn_nyr_launagreidandi_step2_nafn')"
          variant="outlined"
          :readonly="props.disable"
          :error="v$.umsokn.launaName.$error"
          :error-messages="
            v$.umsokn.launaName.$error
              ? [t('umsokn_nyr_launagreidandi_step2_nafn_error')]
              : []
          "
        />
      </v-col>

      <!-- launaSSN (######-####) -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.launaSSN"
          :label="t('umsokn_nyr_launagreidandi_step2_kennitala')"
          variant="outlined"
          :readonly="props.disable"
          type="text"
          inputmode="numeric"
          :counter="11"
          @update:model-value="val => (umsokn.launaSSN = formatKennitala(val))"
          :error="v$.umsokn.launaSSN.$error"
          :error-messages="
            v$.umsokn.launaSSN.$error
              ? [t('umsokn_nyr_launagreidandi_step2_kennitala_error')]
              : []
          "
        />
      </v-col>
    </v-row>

    <v-row>
      <!-- launaAddress -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.launaAddress"
          :label="t('umsokn_nyr_launagreidandi_step2_heimilisfang')"
          variant="outlined"
          :readonly="props.disable"
          :error="v$.umsokn.launaAddress.$error"
          :error-messages="
            v$.umsokn.launaAddress.$error
              ? [t('umsokn_nyr_launagreidandi_step2_heimilisfang_error')]
              : []
          "
        />
      </v-col>

      <!-- launaPostalCode -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.launaPostalCode"
          :label="t('umsokn_nyr_launagreidandi_step2_postnumer')"
          variant="outlined"
          :readonly="props.disable"
          :error="v$.umsokn.launaPostalCode.$error"
          :error-messages="
            v$.umsokn.launaPostalCode.$error
              ? [t('umsokn_nyr_launagreidandi_step2_postnumer_error')]
              : []
          "
        />
      </v-col>
    </v-row>

    <v-row>
      <!-- launaEmail (optional) -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.launaEmail"
          :label="t('umsokn_nyr_launagreidandi_step2_netfang')"
          variant="outlined"
          :readonly="props.disable"
          type="email"
        />
      </v-col>

      <!-- launaPhone (digits only) -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.launaPhone"
          :label="t('umsokn_nyr_launagreidandi_step2_simanumer')"
          variant="outlined"
          :readonly="props.disable"
          type="tel"
          inputmode="numeric"
          @update:model-value="val => (umsokn.launaPhone = digitsOnly(val))"
          :error="v$.umsokn.launaPhone.$error"
          :error-messages="
            v$.umsokn.launaPhone.$error
              ? [t('umsokn_nyr_launagreidandi_step2_simanumer_error')]
              : []
          "
        />
      </v-col>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
.m-b-40 { margin-bottom: 40px; }

.forms__header {
  color: #194458;
  font-weight: 900;
  font-size: 28px;
  line-height: 1.25;
  margin: 0 0 12px;
}

.error__text {
  color: #b00020; /* Vuetify default error color */
  font-size: 0.875rem;
  margin-top: 4px;
}
</style>
